// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: staff editor for assistant connectors: the built-in GitHub one and any number of
// custom API connectors. Actions are edited as rows; "Edit as JSON" covers anything the rows
// don't (object and array parameters with their own JSON schema). Access is a member grid of
// Read/Create/Update/Delete, plus optional "only these members" per action.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import AssistantCapabilities, {
	type ConnectorMember,
	capabilityError,
} from '@app/features/konvo_connections/state/AssistantCapabilities';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {CopyButton} from '@app/features/ui/components/CopyButton';
import {Input, Textarea} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {CaretDownIcon, PlusIcon, TrashIcon, UsersThreeIcon, XIcon} from '@app/features/ui/icons/Icons';
import {Chip, IconButton, Notice, Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import {
	type AssistantConnectorDefinition,
	actionRight,
	type ConnectorAccess,
	type ConnectorCapability,
	type ConnectorParameter,
	type ConnectorRight,
	type ConnectorRights,
	NO_CONNECTOR_ACCESS,
} from '@konvo/schema/src/domains/konvo/AssistantConnector';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {toJS} from 'mobx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';
import {
	blankAction,
	type Method,
	normalizeConnector,
	type Place,
	pathParameters,
	placeOf,
} from '../utils/AssistantConnectorDraft';
import styles from './AssistantConnectorEditor.module.css';
import connectorStyles from './KonvoConnectorCard.module.css';

const METHODS: ReadonlyArray<Method> = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const RIGHTS: ReadonlyArray<ConnectorRight> = ['read', 'create', 'update', 'delete'];
type MemberOf = (id: string) => ConnectorMember;

export const MemberAvatar = ({member, size = 24}: {member: ConnectorMember; size?: number}) => (
	<img
		className={styles.avatar}
		src={getUserAvatarURL({id: member.user_id, avatar: member.avatar})}
		width={size}
		height={size}
		alt=""
		loading="lazy"
	/>
);

/** Discord-style picker: the member list opens on focus, typing narrows it, clicking adds. */
export const MemberPicker = ({
	members,
	exclude,
	onPick,
	label,
}: {
	members: ReadonlyArray<ConnectorMember>;
	exclude: ReadonlyArray<string>;
	onPick: (id: string) => void;
	label: string;
}) => {
	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const list = useRef<HTMLUListElement>(null);
	// The picker sits low in a scrolling modal; bring the opened list into view.
	useEffect(() => {
		if (open) list.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
	}, [open]);
	const q = query.trim().toLowerCase();
	const matches = members
		.filter((m) => !exclude.includes(m.user_id))
		.filter((m) => !q || m.name.toLowerCase().includes(q) || m.username?.toLowerCase().includes(q));
	const pick = (id: string) => {
		onPick(id);
		setQuery('');
	};
	return (
		<div className={styles.picker}>
			<Input
				aria-label={label}
				placeholder={label}
				value={query}
				onFocus={() => setOpen(true)}
				onBlur={() => setOpen(false)}
				onChange={(e) => {
					setQuery(e.target.value);
					setOpen(true);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && matches[0]) {
						e.preventDefault();
						pick(matches[0].user_id);
					} else if (e.key === 'Escape' && open) {
						// Close the list, not the whole editor.
						e.stopPropagation();
						setOpen(false);
					}
				}}
			/>
			{open && (
				// Keep focus in the input while choosing so the list stays open for several picks.
				<ul ref={list} className={styles.pickerResults} onMouseDown={(e) => e.preventDefault()}>
					{matches.length === 0 ? (
						<li className={styles.pickerEmpty}>
							{members.length === 0 ? <Trans>Loading members…</Trans> : <Trans>No matching members</Trans>}
						</li>
					) : (
						matches.map((m) => (
							<li key={m.user_id}>
								<button type="button" onClick={() => pick(m.user_id)}>
									<MemberAvatar member={m} />
									<span className={styles.pickerName}>{m.name}</span>
									{m.username && <span className={styles.pickerUsername}>@{m.username}</span>}
								</button>
							</li>
						))
					)}
				</ul>
			)}
		</div>
	);
};

const AccessGrid = ({
	access,
	members,
	memberOf,
	onChange,
}: {
	access: ConnectorAccess;
	members: ReadonlyArray<ConnectorMember>;
	memberOf: MemberOf;
	onChange: (next: ConnectorAccess) => void;
}) => {
	const {i18n} = useLingui();
	const titles: Record<ConnectorRight, string> = {
		read: i18n._(msg`Read`),
		create: i18n._(msg`Create`),
		update: i18n._(msg`Update`),
		delete: i18n._(msg`Delete`),
	};
	const row = (
		key: string,
		who: string,
		rights: ConnectorRights,
		set: (next: ConnectorRights) => void,
		remove?: () => void,
		member?: ConnectorMember,
	) => (
		<tr key={key}>
			<th scope="row">
				<span className={styles.who}>
					{member ? (
						<MemberAvatar member={member} size={28} />
					) : (
						<span className={styles.everyoneIcon}>
							<UsersThreeIcon size={16} />
						</span>
					)}
					<span className={styles.whoName}>
						{who}
						{member?.username && <span className={styles.pickerUsername}>@{member.username}</span>}
					</span>
				</span>
			</th>
			{RIGHTS.map((right) => (
				<td key={right}>
					<Checkbox
						size="small"
						checked={rights[right]}
						aria-label={`${who}: ${titles[right]}`}
						onChange={(checked) => set({...rights, [right]: checked})}
					/>
				</td>
			))}
			<td>
				{remove && <IconButton label={i18n._(msg`Remove ${who}`)} icon={<TrashIcon size={16} />} onClick={remove} />}
			</td>
		</tr>
	);
	return (
		<div className={styles.field}>
			<div className={styles.gridScroll}>
				<table className={styles.grid}>
					<thead>
						<tr>
							<th scope="col">
								<Trans>Member</Trans>
							</th>
							{RIGHTS.map((right) => (
								<th scope="col" key={right}>
									{titles[right]}
								</th>
							))}
							<th scope="col">
								<span className={styles.srOnly}>
									<Trans>Remove</Trans>
								</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{row('everyone', i18n._(msg`Everyone else`), access.everyone, (everyone) =>
							onChange({...access, everyone}),
						)}
						{access.members.map((m) =>
							row(
								m.user_id,
								memberOf(m.user_id).name,
								m,
								(rights) =>
									onChange({
										...access,
										members: access.members.map((x) => (x.user_id === m.user_id ? {...rights, user_id: m.user_id} : x)),
									}),
								() => onChange({...access, members: access.members.filter((x) => x.user_id !== m.user_id)}),
								memberOf(m.user_id),
							),
						)}
					</tbody>
				</table>
			</div>
			<MemberPicker
				members={members}
				exclude={access.members.map((m) => m.user_id)}
				label={i18n._(msg`Add a member…`)}
				onPick={(user_id) =>
					onChange({
						...access,
						members: [...access.members, {user_id, read: true, create: false, update: false, delete: false}],
					})
				}
			/>
			<p className={styles.hint}>
				<Trans>
					Staff always have full access. GET actions count as Read, POST as Create, PUT and PATCH as Update, and DELETE
					as Delete. Anyone with nothing ticked doesn't see the connector.
				</Trans>
			</p>
		</div>
	);
};
const PATH_EXAMPLE = '/items/{item_id}';

const ParameterRow = ({
	action,
	parameter,
	onChange,
	onRemove,
}: {
	action: ConnectorCapability;
	parameter: ConnectorParameter;
	onChange: (next: ConnectorParameter, place?: Place) => void;
	onRemove: () => void;
}) => {
	const {i18n} = useLingui();
	const place = placeOf(action, parameter.name);
	const structured = parameter.type === 'object' || parameter.type === 'array';
	return (
		<div className={styles.parameter}>
			<div className={styles.parameterFields}>
				<Input
					aria-label={i18n._(msg`Parameter name`)}
					placeholder={i18n._(msg`name`)}
					value={parameter.name}
					disabled={place === 'path'}
					onChange={(e) => onChange({...parameter, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '')})}
				/>
				<Input
					aria-label={i18n._(msg`What it means`)}
					placeholder={i18n._(msg`What it means, e.g. Search text`)}
					value={parameter.label}
					onChange={(e) => onChange({...parameter, label: e.target.value})}
				/>
			</div>
			<div className={styles.parameterOptions}>
				{structured ? (
					<span className={styles.badge}>{parameter.type === 'object' ? 'JSON object' : 'JSON list'}</span>
				) : (
					<Segmented<'string' | 'number' | 'boolean'>
						compact
						value={parameter.type as 'string' | 'number' | 'boolean'}
						options={[
							{value: 'string', children: i18n._(msg`Text`)},
							{value: 'number', children: i18n._(msg`Number`)},
							{value: 'boolean', children: i18n._(msg`Yes/no`)},
						]}
						onChange={(type) =>
							onChange({...parameter, type, choices: type === 'string' ? parameter.choices : undefined})
						}
						aria-label={i18n._(msg`Parameter type`)}
					/>
				)}
				{place === 'path' ? (
					<span className={styles.badge}>
						<Trans>In the path</Trans>
					</span>
				) : action.method === 'GET' || structured ? (
					<span className={styles.badge}>{structured ? i18n._(msg`In the body`) : i18n._(msg`In the query`)}</span>
				) : (
					<Segmented<'query' | 'body'>
						compact
						value={place}
						options={[
							{value: 'query', children: i18n._(msg`Query`)},
							{value: 'body', children: i18n._(msg`Body`)},
						]}
						onChange={(next) => onChange(parameter, next)}
						aria-label={i18n._(msg`Where it is sent`)}
					/>
				)}
				<Chip
					pressed={parameter.required || place === 'path'}
					disabled={place === 'path'}
					onClick={() => onChange({...parameter, required: !parameter.required})}
				>
					<Trans>Required</Trans>
				</Chip>
				{place !== 'path' && (
					<IconButton label={i18n._(msg`Remove parameter`)} icon={<TrashIcon size={16} />} onClick={onRemove} />
				)}
			</div>
		</div>
	);
};

const ActionRow = ({
	action,
	open,
	onToggle,
	onChange,
	onRemove,
	only,
	onOnlyChange,
	members,
	memberOf,
}: {
	action: ConnectorCapability;
	open: boolean;
	onToggle: () => void;
	onChange: (next: ConnectorCapability) => void;
	onRemove: () => void;
	/** Members this action is pinned to; null follows the access grid. */
	only: Array<string> | null;
	onOnlyChange: (next: Array<string> | null) => void;
	members: ReadonlyArray<ConnectorMember>;
	memberOf: MemberOf;
}) => {
	const {i18n} = useLingui();
	const right = {
		read: i18n._(msg`Read`),
		create: i18n._(msg`Create`),
		update: i18n._(msg`Update`),
		delete: i18n._(msg`Delete`),
	}[actionRight(action.method)];
	const setParameter = (index: number, next: ConnectorParameter, place?: Place) => {
		const previous = action.parameters[index].name;
		const rename = (list: Array<string>) => list.map((n) => (n === previous ? next.name : n));
		let query = rename(action.query);
		let body = rename(action.body);
		if (place === 'query') {
			body = body.filter((n) => n !== next.name);
			query = [...query.filter((n) => n !== next.name), next.name];
		} else if (place === 'body') {
			query = query.filter((n) => n !== next.name);
			body = [...body.filter((n) => n !== next.name), next.name];
		}
		onChange({
			...action,
			query,
			body,
			scope: rename(action.scope),
			parameters: action.parameters.map((p, i) => (i === index ? next : p)),
		});
	};
	const setPath = (path: string) => {
		const parameters = [...action.parameters];
		for (const name of pathParameters(path))
			if (!parameters.some((p) => p.name === name))
				parameters.push({name, label: name, type: 'string', required: true});
		onChange({...action, path, parameters});
	};
	return (
		<li className={clsx(styles.action, open && styles.actionOpen)}>
			<button type="button" className={styles.actionHeader} aria-expanded={open} onClick={onToggle}>
				<span className={clsx(styles.method, action.method !== 'GET' && styles.methodWrite)}>{action.method}</span>
				<span className={styles.actionTitle}>
					<span className={styles.actionLabel}>{action.label || i18n._(msg`New action`)}</span>
					<span className={styles.actionPath}>{action.path}</span>
				</span>
				{only && (
					<span className={styles.badgeSmall}>
						{only.length === 1 ? memberOf(only[0]).name : i18n._(msg`${only.length} members`)}
					</span>
				)}
				<CaretDownIcon size={16} className={styles.caret} />
			</button>
			{open && (
				<div className={styles.actionBody}>
					<div className={styles.twoColumns}>
						<Input
							label={i18n._(msg`Name`)}
							placeholder={i18n._(msg`Search knowledge`)}
							value={action.label}
							onChange={(e) => onChange({...action, label: e.target.value})}
						/>
						<div className={styles.field}>
							<span className={styles.fieldLabel}>
								<Trans>Method</Trans>
							</span>
							<Segmented<Method>
								compact
								value={action.method}
								options={METHODS.map((m) => ({value: m, children: m}))}
								onChange={(method) => onChange({...action, method})}
								aria-label={i18n._(msg`HTTP method`)}
							/>
						</div>
					</div>
					<Input
						label={i18n._(msg`Path`)}
						placeholder={PATH_EXAMPLE}
						value={action.path}
						className={styles.mono}
						footer={
							<p className={styles.hint}>
								<Trans>Added to the base URL. Put values the assistant fills in inside braces:</Trans>{' '}
								<code>{PATH_EXAMPLE}</code>
							</p>
						}
						onChange={(e) => setPath(e.target.value)}
					/>
					<Textarea
						label={i18n._(msg`When to use it`)}
						placeholder={i18n._(msg`Tells the assistant what this returns or does.`)}
						value={action.description}
						minRows={2}
						maxRows={5}
						onChange={(e) => onChange({...action, description: e.target.value})}
					/>
					<div className={styles.field}>
						<span className={styles.fieldLabel}>
							<Trans>Parameters</Trans>
						</span>
						{action.parameters.length === 0 && (
							<p className={styles.hint}>
								<Trans>No parameters. The assistant calls this action as it is.</Trans>
							</p>
						)}
						{action.parameters.map((p, index) => (
							<ParameterRow
								key={index}
								action={action}
								parameter={p}
								onChange={(next, place) => setParameter(index, next, place)}
								onRemove={() =>
									onChange({
										...action,
										parameters: action.parameters.filter((_, i) => i !== index),
										query: action.query.filter((n) => n !== p.name),
										body: action.body.filter((n) => n !== p.name),
										scope: action.scope.filter((n) => n !== p.name),
									})
								}
							/>
						))}
						<div>
							<Button
								variant="secondary"
								small
								leftIcon={<PlusIcon size={14} />}
								onClick={() =>
									onChange({
										...action,
										parameters: [...action.parameters, {name: '', label: '', type: 'string', required: false}],
									})
								}
							>
								<Trans>Add parameter</Trans>
							</Button>
						</div>
					</div>
					<div className={styles.field}>
						<span className={styles.fieldLabel}>
							<Trans>Who can use it</Trans>
						</span>
						<Segmented<'grid' | 'only'>
							compact
							className={styles.segmented}
							value={only ? 'only' : 'grid'}
							options={[
								{value: 'grid', children: i18n._(msg`Everyone with ${right} access`)},
								{value: 'only', children: i18n._(msg`Only specific members`)},
							]}
							onChange={(value) => onOnlyChange(value === 'only' ? [] : null)}
							aria-label={i18n._(msg`Who can use this action`)}
						/>
						{only && (
							<>
								{only.length > 0 ? (
									<div className={styles.chips}>
										{only.map((id) => (
											<span className={styles.memberChip} key={id}>
												<MemberAvatar member={memberOf(id)} size={20} />
												{memberOf(id).name}
												<button
													type="button"
													aria-label={i18n._(msg`Remove ${memberOf(id).name}`)}
													onClick={() => onOnlyChange(only.filter((x) => x !== id))}
												>
													<XIcon size={12} />
												</button>
											</span>
										))}
									</div>
								) : (
									<p className={styles.hint}>
										<Trans>Nobody but staff can use this action until you add someone.</Trans>
									</p>
								)}
								<MemberPicker
									members={members}
									exclude={only}
									label={i18n._(msg`Add a member…`)}
									onPick={(id) => onOnlyChange([...only, id])}
								/>
							</>
						)}
					</div>
					{action.method !== 'GET' && (
						<Notice>
							<Trans>
								This action changes data, so the assistant asks for approval before each call. Automations need it
								approved once, with the path fixed.
							</Trans>
						</Notice>
					)}
					<div>
						<Button variant="ghost" small leftIcon={<TrashIcon size={14} />} onClick={onRemove}>
							<Trans>Remove action</Trans>
						</Button>
					</div>
				</div>
			)}
		</li>
	);
};

export const AssistantConnectorEditorModal = observer(
	({definition, version}: {definition: AssistantConnectorDefinition; version: number | null}) => {
		const {i18n} = useLingui();
		const [draft, setDraft] = useState<AssistantConnectorDefinition>(() => toJS(definition));
		const [secret, setSecret] = useState('');
		const [open, setOpen] = useState<number | null>(null);
		const [json, setJson] = useState<string | null>(null);
		const [busy, setBusy] = useState(false);
		const [error, setError] = useState('');
		const creating = version == null;
		const github = draft.provider === 'github';
		const current = AssistantCapabilities.connectors.find((c) => c.definition.id === definition.id);
		const [access, setAccess] = useState<ConnectorAccess>(() => toJS(current?.access) ?? NO_CONNECTOR_ACCESS);
		// Per-action "only these members", kept beside the rows (new actions have no ID until saved).
		const [only, setOnly] = useState<Array<Array<string> | null>>(() =>
			definition.capabilities.map((c) => access.actions[c.id] ?? null),
		);
		useEffect(() => {
			void AssistantCapabilities.refreshMembers().catch(() => {});
		}, []);
		const memberOf: MemberOf = (id) =>
			AssistantCapabilities.members.find((m) => m.user_id === id) ??
			current?.access_members?.find((m) => m.user_id === id) ?? {user_id: id, name: id, username: null, avatar: null};
		const taken = new Set(AssistantCapabilities.connectors.map((c) => c.definition.id));
		const patch = (value: Partial<AssistantConnectorDefinition>) => setDraft((d) => ({...d, ...value}));
		const close = () => ModalCommands.pop();
		const actions = () => {
			if (json == null) return draft.capabilities;
			const parsed = JSON.parse(json);
			if (!Array.isArray(parsed)) throw new Error(i18n._(msg`The actions JSON must be a list.`));
			return parsed as Array<ConnectorCapability>;
		};
		const toggleJson = () => {
			setError('');
			if (json == null) {
				setJson(JSON.stringify(draft.capabilities, null, 2));
				return;
			}
			try {
				const byId = Object.fromEntries(draft.capabilities.map((c, i) => [c.id, only[i]]));
				const next = actions();
				patch({capabilities: next});
				setOnly(next.map((c) => (c.id ? (byId[c.id] ?? null) : null)));
				setJson(null);
			} catch (e) {
				setError(capabilityError(e, i18n._(msg`The actions JSON is not valid.`)));
			}
		};
		const save = async () => {
			setBusy(true);
			setError('');
			try {
				const capabilities = actions();
				const byId = Object.fromEntries(draft.capabilities.map((c, i) => [c.id, only[i]]));
				const next = normalizeConnector({...draft, capabilities}, taken);
				const pinned = next.capabilities.flatMap((c, i) => {
					const members = json == null ? only[i] : byId[capabilities[i].id];
					return members ? [[c.id, members] as const] : [];
				});
				const nextAccess = {...access, actions: Object.fromEntries(pinned)};
				const changed = JSON.stringify(next) !== JSON.stringify(normalizeConnector(toJS(definition), taken));
				// Access alone keeps the version, so approved automations stay approved.
				if (creating || changed || secret)
					await AssistantCapabilities.configure(next, version, secret || undefined, nextAccess);
				else await AssistantCapabilities.saveAccess(definition.id, nextAccess);
				close();
			} catch (e) {
				setError(capabilityError(e, i18n._(msg`The connector could not be saved.`)));
			} finally {
				setBusy(false);
			}
		};
		const remove = () =>
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={i18n._(msg`Delete ${draft.name}?`)}
						description={i18n._(
							msg`The assistant loses access to it straight away, saved tokens are removed and automations that use it stop at that step.`,
						)}
						primaryText={i18n._(msg`Delete connector`)}
						primaryVariant="danger"
						onPrimary={async () => {
							await AssistantCapabilities.remove(definition.id);
							close();
						}}
					/>
				)),
			);
		const restore = async () => {
			setError('');
			try {
				const defaults = await AssistantCapabilities.githubDefaults();
				patch({capabilities: defaults.capabilities, base_url: defaults.base_url, description: defaults.description});
				setOnly(defaults.capabilities.map((c) => access.actions[c.id] ?? null));
				setJson(null);
				setOpen(null);
			} catch (e) {
				setError(capabilityError(e, i18n._(msg`The GitHub defaults could not be loaded.`)));
			}
		};
		return (
			<Modal.Root size="large" onClose={close}>
				<Modal.Header
					title={
						creating
							? i18n._(msg`New custom connector`)
							: github
								? i18n._(msg`GitHub connector`)
								: i18n._(msg`${definition.name} connector`)
					}
				/>
				<Modal.Content>
					<div className={styles.editor}>
						<section className={styles.group}>
							<h3 className={styles.groupTitle}>
								<Trans>API</Trans>
							</h3>
							<div className={styles.twoColumns}>
								<Input
									label={i18n._(msg`Name`)}
									placeholder={i18n._(msg`e.g. Lore`)}
									value={draft.name}
									autoFocus={creating}
									onChange={(e) => patch({name: e.target.value})}
								/>
								<Input
									label={i18n._(msg`Base URL`)}
									placeholder="https://api.example.com/v1"
									value={draft.base_url}
									className={styles.mono}
									onChange={(e) => patch({base_url: e.target.value})}
								/>
							</div>
							<Input
								label={i18n._(msg`Description`)}
								placeholder={i18n._(msg`What people and the assistant can do with it`)}
								value={draft.description}
								onChange={(e) => patch({description: e.target.value})}
							/>
							{!creating && (
								<p className={styles.hint}>
									<Trans>Connector ID</Trans> <code>{draft.id}</code>
								</p>
							)}
						</section>

						<section className={styles.group}>
							<h3 className={styles.groupTitle}>
								<Trans>Sign-in</Trans>
							</h3>
							{github ? (
								<p className={styles.hint}>
									{draft.auth === 'oauth2' ? (
										<Trans>
											Everyone connects by signing in to GitHub through the Konvo GitHub App. What the assistant can
											reach is set by the app's permissions and the repositories it is installed on.
										</Trans>
									) : (
										<Trans>
											Each person pastes their own token. Set up the Konvo GitHub App on the server to let people sign
											in instead.
										</Trans>
									)}{' '}
									{AssistantCapabilities.githubAppUrl && (
										<button
											type="button"
											className={connectorStyles.inlineLink}
											onClick={() => void openExternalUrl(AssistantCapabilities.githubAppUrl!)}
										>
											<Trans>Open the GitHub App</Trans>
										</button>
									)}
								</p>
							) : (
								<>
									<Segmented<'bearer' | 'oauth2'>
										compact
										className={styles.segmented}
										value={draft.auth}
										options={[
											{value: 'bearer', children: i18n._(msg`Access token`)},
											{value: 'oauth2', children: i18n._(msg`OAuth sign-in`)},
										]}
										onChange={(auth) => patch({auth})}
										aria-label={i18n._(msg`Sign-in method`)}
									/>
									{draft.auth === 'bearer' ? (
										<p className={styles.hint}>
											{draft.shared ? (
												<Trans>
													Staff paste one token that everyone shares. It is stored encrypted and never shown again.
												</Trans>
											) : (
												<Trans>
													Each person pastes their own token when they connect. Tokens are stored encrypted.
												</Trans>
											)}
										</p>
									) : (
										<>
											<div className={styles.twoColumns}>
												<Input
													label={i18n._(msg`Client ID`)}
													value={draft.client_id}
													onChange={(e) => patch({client_id: e.target.value})}
												/>
												<Input
													type="password"
													autoComplete="off"
													label={i18n._(msg`Client secret`)}
													placeholder={
														current?.client_secret_saved
															? i18n._(msg`Saved. Enter a new one to replace it.`)
															: i18n._(msg`Optional for PKCE apps`)
													}
													value={secret}
													onChange={(e) => setSecret(e.target.value)}
												/>
											</div>
											<div className={styles.twoColumns}>
												<Input
													label={i18n._(msg`Authorization URL`)}
													className={styles.mono}
													value={draft.authorize_url ?? ''}
													onChange={(e) => patch({authorize_url: e.target.value || undefined})}
												/>
												<Input
													label={i18n._(msg`Token URL`)}
													className={styles.mono}
													value={draft.token_url ?? ''}
													onChange={(e) => patch({token_url: e.target.value || undefined})}
												/>
											</div>
											<Input
												label={i18n._(msg`Scopes`)}
												placeholder="read write"
												className={styles.mono}
												value={draft.scopes}
												onChange={(e) => patch({scopes: e.target.value})}
											/>
											<div className={styles.callback}>
												<span className={styles.fieldLabel}>
													<Trans>Callback URL for the provider's app settings</Trans>
												</span>
												<div className={styles.callbackValue}>
													<code>{AssistantCapabilities.oauthCallbackUrl}</code>
													<CopyButton value={AssistantCapabilities.oauthCallbackUrl} label={msg`Copy callback URL`} />
												</div>
											</div>
										</>
									)}
								</>
							)}
						</section>

						<section className={styles.group}>
							<h3 className={styles.groupTitle}>
								<Trans>Access</Trans>
							</h3>
							<div className={styles.switches}>
								<Switch
									label={i18n._(msg`Turned on`)}
									description={i18n._(msg`When off, nobody can connect it and the assistant can't use it.`)}
									value={draft.enabled}
									onChange={(enabled) => patch({enabled})}
								/>
								<Switch
									label={i18n._(msg`One shared connection`)}
									description={i18n._(
										msg`Everyone uses the organization's credentials. Only staff can connect or disconnect it.`,
									)}
									value={draft.shared}
									onChange={(shared) => patch({shared})}
								/>
								{!github && (
									<Switch
										label={i18n._(msg`Private network`)}
										description={i18n._(msg`Allow Konvo's server to reach this API on the internal network.`)}
										value={draft.network === 'private'}
										onChange={(value) => patch({network: value ? 'private' : 'public'})}
									/>
								)}
							</div>
						</section>

						<section className={styles.group}>
							<h3 className={styles.groupTitle}>
								<Trans>Who can see and use it</Trans>
							</h3>
							<AccessGrid
								access={access}
								members={AssistantCapabilities.members}
								memberOf={memberOf}
								onChange={setAccess}
							/>
						</section>

						<section className={styles.group}>
							<div className={styles.groupHeader}>
								<h3 className={styles.groupTitle}>
									<Trans>Actions</Trans>
								</h3>
								<Button variant="ghost" small onClick={toggleJson}>
									{json == null ? <Trans>Edit as JSON</Trans> : <Trans>Edit as list</Trans>}
								</Button>
							</div>
							<p className={styles.hint}>
								<Trans>The assistant can only call the actions listed here. GET actions only read data.</Trans>
							</p>
							{json != null ? (
								<Textarea
									label={i18n._(msg`Actions as JSON`)}
									value={json}
									minRows={10}
									maxRows={24}
									className={styles.mono}
									onChange={(e) => setJson(e.target.value)}
								/>
							) : (
								<>
									{draft.capabilities.length > 0 && (
										<ul className={styles.actions}>
											{draft.capabilities.map((c, index) => (
												<ActionRow
													key={index}
													action={c}
													open={open === index}
													onToggle={() => setOpen(open === index ? null : index)}
													onChange={(next) =>
														patch({capabilities: draft.capabilities.map((a, i) => (i === index ? next : a))})
													}
													onRemove={() => {
														patch({capabilities: draft.capabilities.filter((_, i) => i !== index)});
														setOnly(only.filter((_, i) => i !== index));
														setOpen(null);
													}}
													only={only[index] ?? null}
													onOnlyChange={(next) => setOnly(only.map((o, i) => (i === index ? next : o)))}
													members={AssistantCapabilities.members}
													memberOf={memberOf}
												/>
											))}
										</ul>
									)}
									<div>
										<Button
											variant="secondary"
											small
											leftIcon={<PlusIcon size={14} />}
											onClick={() => {
												patch({capabilities: [...draft.capabilities, blankAction()]});
												setOnly([...only, null]);
												setOpen(draft.capabilities.length);
											}}
										>
											<Trans>Add action</Trans>
										</Button>
									</div>
								</>
							)}
						</section>
						{error && <Notice tone="danger">{error}</Notice>}
					</div>
				</Modal.Content>
				<Modal.Footer>
					<div className={styles.footer}>
						{github ? (
							<Button variant="ghost" onClick={() => void restore()}>
								<Trans>Restore GitHub defaults</Trans>
							</Button>
						) : (
							!creating && (
								<Button variant="danger" onClick={remove}>
									<Trans>Delete</Trans>
								</Button>
							)
						)}
						<span className={styles.spacer} />
						<Button variant="secondary" onClick={close}>
							<Trans>Cancel</Trans>
						</Button>
						<Button
							submitting={busy}
							disabled={!draft.name.trim() || !draft.base_url.trim()}
							onClick={() => void save()}
						>
							{creating ? <Trans>Add connector</Trans> : <Trans>Save</Trans>}
						</Button>
					</div>
				</Modal.Footer>
			</Modal.Root>
		);
	},
);

export function openConnectorEditor(definition: AssistantConnectorDefinition, version: number | null) {
	ModalCommands.push(modal(() => <AssistantConnectorEditorModal definition={definition} version={version} />));
}
